import { useCallback } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

import type { Auth0Adapter } from './useAuth0Adapter.types'
import { getAuth0UserInfo } from '../contexts/AuthContext.web'
import {
  isIgnorableAuthSessionError,
  MissingAccessTokenError,
} from '../utils/auth-errors'

export type { Auth0Adapter, User } from './useAuth0Adapter.types'

export function useAuth0Adapter(): Auth0Adapter {
  const {
    user,
    isLoading,
    error: auth0Error,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0()

  const error: Error | undefined = auth0Error ?? undefined
  const authorize = useCallback(
    async (options?: {
      screen_hint?: string
      prompt?: 'login' | 'none' | 'consent' | 'select_account'
      returnTo?: string
    }) => {
      await loginWithRedirect({
        appState: { returnTo: options?.returnTo },
        authorizationParams: {
          screen_hint: options?.screen_hint,
          prompt: options?.prompt,
        },
        openUrl: (url: string) => window.location.assign(url),
      })
    },
    [loginWithRedirect],
  )

  const clearSession = useCallback(async () => {
    try {
      await logout({
        openUrl: false,
      })
    } catch (e) {
      console.error('Logout failed', e)
      throw e
    }
  }, [logout])

  const getCredentials = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      return { accessToken: token, idToken: null }
    } catch (e) {
      if (isIgnorableAuthSessionError(e)) {
        return { accessToken: null, idToken: null }
      }

      console.error('Error getting credentials', e)
      return { accessToken: null, idToken: null }
    }
  }, [getAccessTokenSilently])

  const getUserInfo = useCallback(async (): Promise<Auth0Adapter['user']> => {
    const { accessToken } = await getCredentials()
    if (!accessToken) throw new MissingAccessTokenError()
    const userInfo = await getAuth0UserInfo(accessToken)

    return {
      email: userInfo.email,
      emailVerified: userInfo.email_verified,
      givenName: userInfo.given_name,
      name: userInfo.name,
      nickname: userInfo.nickname,
      updatedAt: userInfo.updated_at,
      sub: userInfo.sub,
    }
  }, [getCredentials])

  return {
    user: user
      ? {
          sub: user.sub,
          email: user.email,
          emailVerified: user.email_verified,
          updatedAt: user.updated_at,
          givenName: user.given_name,
          nickname: user.nickname,
          name: user.name,
        }
      : null,
    isLoading,
    error,
    authorize,
    clearSession,
    getUserInfo,
    getCredentials,
  }
}
